import { languages } from "../i18n.config";
import { BCP47LangCode } from "../i18n.types";

/**
 * Maps a base (language-only) tag to the supported locale we ship for it, so a
 * device reporting e.g. `es`, `de-AT`, or `pt` resolves to the closest option.
 */
const BASE_LANGUAGE_TO_LOCALE: Record<string, BCP47LangCode> = {
  en: "en-US",
  es: "es-MX",
  fr: "fr-FR",
  de: "de-DE",
  it: "it-IT",
  ja: "ja-JP",
  ko: "ko-KR",
  vi: "vi-VN",
  tl: "tl-PH",
  fil: "tl-PH",
  zh: "zh-Hans",
};

/**
 * Region-specific mappings that take precedence over the base-language default,
 * so a device reporting e.g. `en-AU` or `fr-BE` lands on the closest variant we
 * ship. Only regions whose closest option is NOT the base default are listed.
 */
const REGION_TO_LOCALE: Record<string, BCP47LangCode> = {
  // English variants closest to British spelling/vocabulary
  "en-au": "en-GB",
  "en-nz": "en-GB",
  "en-ie": "en-GB",
  "en-za": "en-GB",
  "en-in": "en-GB",
  "en-gb": "en-GB",
  "en-ca": "en-CA",
  // French: European-French-speaking regions map to fr-FR (the base default);
  // Canadian French is the exact fr-CA match handled above.
};

/**
 * Resolves an arbitrary BCP 47 language tag (e.g. from the device or browser)
 * to one of the app's supported locales.
 *
 * Resolution order:
 *  1. Exact supported match (case-insensitive), e.g. `fr-CA` → `fr-CA`.
 *  2. Chinese script handling: any `zh-Hant`/`zh-TW`/`zh-HK`/`zh-MO` →
 *     `zh-Hant`; all other `zh` variants → `zh-Hans`.
 *  3. Base-language fallback, e.g. `de-AT` → `de-DE`, `es` → `es-MX`.
 *
 * Returns `undefined` when nothing supported matches, so callers can fall back
 * to the global default.
 */
export function resolveSupportedLocale(tag: string | null | undefined): BCP47LangCode | undefined {
  if (!tag) return undefined;

  const normalized = tag.trim().toLowerCase();
  if (!normalized) return undefined;

  const exact = languages.find((locale) => locale.toLowerCase() === normalized);
  if (exact) return exact;

  const [base, ...rest] = normalized.split("-");

  if (base === "zh") {
    const subtags = rest.join("-");
    const isTraditional =
      subtags.includes("hant") ||
      subtags.includes("tw") ||
      subtags.includes("hk") ||
      subtags.includes("mo");
    return isTraditional ? "zh-Hant" : "zh-Hans";
  }

  const region = REGION_TO_LOCALE[normalized];
  if (region) return region;

  return BASE_LANGUAGE_TO_LOCALE[base];
}
